import React, { useEffect, useState } from 'react';
import { USER_ROLE } from '@app/consts/user_consts';
import { setSidebarCollapsed, toggleSidebar } from '@app/features/layoutSlice';
import { useLocalStorageSync } from '@app/hooks/useLocalStorageSync';
import { NAVIGATE_ROUTE } from '@app/modules/routes/common-route';
import { UserUtils } from '@app/modules/userinfo';
import chartlineIcon from '@src/assets/imgs/icons/Chartline.png';
import chartpieIcon from '@src/assets/imgs/icons/Chartpie.png';
import chevronIconSideMenu from '@src/assets/imgs/icons/chevron-right-side-menu.svg';
import documentIcon from '@src/assets/imgs/icons/Document.png';
import fileIcon from '@src/assets/imgs/icons/file.png';
import settingIcon from '@src/assets/imgs/icons/Settings.png';
import vectorIcon from '@src/assets/imgs/icons/Vector.png';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AppMenuLarge from './AppMenuLarge';
import AppMenuSmall from './AppMenuSmall';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const collapsed = useSelector((state: any) => state.layout.sidebarCollapsed);
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [isMenuVisible, setIsMenuVisible] = useState(true);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const savedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (savedCollapsed !== null) {
      dispatch(setSidebarCollapsed(savedCollapsed === 'true'));
    }
  }, [dispatch]);

  const [projectId] = useLocalStorageSync<string | null>('projectId', localStorage.getItem('projectId'));

  const menuModel = [
    {
      label: '',
      items: [
        {
          label: t('menu.SC004'),
          iconImg: chartpieIcon,
          to: NAVIGATE_ROUTE.SC004,
        },
        {
          label: t('menu.SC006'),
          iconImg: vectorIcon,
          to: NAVIGATE_ROUTE.SC006,
          visible: !!projectId,
        },
        {
          label: t('menu.SC005'),
          iconImg: chartlineIcon,
          to: NAVIGATE_ROUTE.SC005,
          visible: !!projectId,
        },
        {
          label: t('menu.SC007'),
          iconImg: documentIcon,
          to: NAVIGATE_ROUTE.SC007,
          visible: !!projectId,
        },
        {
          label: t('menu.projectSettings'),
          iconImg: fileIcon,
          visible: !!projectId,
          items: [
            { label: t('menu.SC008'), to: NAVIGATE_ROUTE.SC008, visible: !!projectId },
            {
              label: t('menu.SC009'),
              to: NAVIGATE_ROUTE.SC009,
              visible: !!projectId,
            },
            {
              label: t('menu.SC010'),
              to: NAVIGATE_ROUTE.SC010,
              visible: !!projectId,
            },
          ],
        },
        {
          label: '',
          separator: true,
          visible: UserUtils.get()?.systemRoles.some((role) => role.roleId === USER_ROLE.BIM_DATA_PLATFORM_ADMIN),
        },
        {
          label: t('menu.systemSettings'),
          iconImg: settingIcon,
          visible: UserUtils.get()?.systemRoles.some((role) => role.roleId === USER_ROLE.BIM_DATA_PLATFORM_ADMIN),
          items: [
            {
              label: t('menu.SC011'),
              to: NAVIGATE_ROUTE.SC011,
              visible: UserUtils.get()?.systemRoles.some((role) => role.roleId === USER_ROLE.BIM_DATA_PLATFORM_ADMIN),
            },
            {
              label: t('menu.SC013'),
              to: NAVIGATE_ROUTE.SC013,
              visible: UserUtils.get()?.systemRoles.some((role) => role.roleId === USER_ROLE.BIM_DATA_PLATFORM_ADMIN),
            },
          ],
        },
      ],
    },
  ];

  useEffect(() => {
    // Patch setItem to dispatch custom event
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key: string, value: string) {
      originalSetItem.call(this, key, value);
      if (key === 'projectId') {
        window.dispatchEvent(new Event('projectIdChange'));
      }
    };

    return () => {
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const handleToggleSidebar = () => {
    setIsButtonVisible(false);
    setIsMenuVisible(false);
    dispatch(toggleSidebar());
    setTimeout(() => {
      setIsButtonVisible(true);
      setIsMenuVisible(true);
    }, 80);
  };

  // Save collapsed state to localStorage and update CSS variable when it changes
  useEffect(() => {
    // collapsed: sidebar-content = 48px, layout-sidebar = 68px
    // expanded: sidebar-content = 240px, layout-sidebar = 260px
    document.documentElement.style.setProperty('--sidebar-width', collapsed ? '68px' : '260px');
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  return (
    <div className={`layout-sidebar ${collapsed ? 'collapsed' : 'expanded'}`}>
      <div className="sidebar-content">
        <button
          className="sidebar-toggle-btn"
          onClick={handleToggleSidebar}
          style={{ opacity: isButtonVisible ? 1 : 0, transition: 'opacity 0.2s' }}
        >
          <img
            src={chevronIconSideMenu}
            alt="Toggle Sidebar"
            className={`sidebar-toggle-icon ${collapsed ? 'sidebar-toggle-icon--collapsed' : 'sidebar-toggle-icon--expanded'}`}
          />
        </button>
        {collapsed ? <AppMenuSmall model={menuModel} /> : <AppMenuLarge model={menuModel} isVisible={isMenuVisible} />}
      </div>
    </div>
  );
};

export default Sidebar;
